<h2 mat-dialog-title>
    <mat-icon class="title-icon entity-hats">school</mat-icon>
    <!-- The raw hat key was rendered here once — never show raw entity keys
         to users (OE-158, Paul's build-45 review). -->
    {{hat?.name || 'Hat'}}
    <button mat-icon-button mat-dialog-close class="close-button">
        <mat-icon>close</mat-icon>
    </button>
</h2>

<mat-dialog-content class="hat-details-content">
    <div class="hat-details-layout">
        <!-- Left Sidebar - Hat Profile -->
        <aside class="profile-sidebar">
            <button
                mat-icon-button
                *ngIf="orgDialogService.mayManageHats"
                class="sidebar-edit-button"
                (click)="openEditDialog()"
                matTooltip="Edit Hat"
            >
                <mat-icon>edit</mat-icon>
            </button>
            <div class="avatar-wrapper">
                <mat-icon class="hat-avatar-icon">school</mat-icon>
            </div>
            <h3 class="hat-name">{{hat?.name || 'Unknown Hat'}}</h3>
            <p class="hat-description" *ngIf="hat?.description">{{hat?.description}}</p>

            <div class="multi-user-indicator" *ngIf="hat?.isMultiUsers || hat?.multiUsers">
                <mat-icon>group</mat-icon>
                <span>Multi-User Hat</span>
            </div>

            <div class="multi-user-indicator special-rights-indicator" *ngIf="hat?.isSpecialRights">
                <mat-icon>shield</mat-icon>
                <span>Special Rights</span>
            </div>

            <div class="quick-stats">
                <div class="stat" *ngIf="usersOnHat.length">
                    <span class="stat-value">{{usersOnHat.length}}</span>
                    <span class="stat-label">Staff</span>
                </div>
                <div class="stat" *ngIf="securityGroups.length">
                    <span class="stat-value">{{securityGroups.length}}</span>
                    <span class="stat-label">Groups</span>
                </div>
                <div class="stat" *ngIf="positions.length">
                    <span class="stat-value">{{positions.length}}</span>
                    <span class="stat-label">Org Parts</span>
                </div>
                <div class="stat" *ngIf="features.length">
                    <span class="stat-value">{{features.length}}</span>
                    <span class="stat-label">Features</span>
                </div>
            </div>
        </aside>

        <!-- Main Content -->
        <main class="details-main">
            <mat-accordion class="sections-accordion" multi>
                <!-- Users Section -->
                <mat-expansion-panel
                    [expanded]="sectionStates.users"
                    (expandedChange)="sectionStates.users = $event"
                    *ngIf="usersOnHat.length || pendingUsersOnHat.length"
                >
                    <mat-expansion-panel-header>
                        <mat-panel-title>
                            <mat-icon class="entity-users">people</mat-icon>
                            Users Wearing This Hat ({{ usersOnHat.length }})
                            <span
                                *ngIf="pendingUsersOnHat.length"
                                style="color: #ff9800; font-size: 12px; margin-left: 8px"
                            >
                                +{{pendingUsersOnHat.length}} pending
                            </span>
                        </mat-panel-title>
                    </mat-expansion-panel-header>
                    <div class="users-grid">
                        <div class="user-chip" *ngFor="let user of usersOnHat">
                            <div class="user-avatar">
                                <img [src]="getUserAvatarUrl(user)" [alt]="user.name" style="object-fit: cover" />
                            </div>
                            <div class="user-info">
                                <span class="user-name">{{user.name}}</span>
                            </div>
                        </div>
                        <!-- Pending Users -->
                        <div
                            class="user-chip pending-chip"
                            [class.pending-chip--acceptance]="user.awaitingAcceptance"
                            [class.pending-chip--approval]="!user.awaitingAcceptance"
                            *ngFor="let user of pendingUsersOnHat"
                            [matTooltip]="user.awaitingAcceptance
                                ? 'Invitation sent — waiting for the user to accept before they can be approved.'
                                : 'Awaiting administrator approval for this hat.'"
                        >
                            <div class="user-avatar">
                                <img
                                    [src]="getUserAvatarUrl(user)"
                                    [alt]="user.name"
                                    style="object-fit: cover; opacity: 0.7"
                                />
                            </div>
                            <div class="user-info">
                                <span class="user-name">{{user.name}}</span>
                                <span class="pending-chip-label">
                                    {{ user.awaitingAcceptance ? 'Pending Acceptance' : 'Pending Approval' }}
                                </span>
                            </div>
                        </div>
                    </div>
                </mat-expansion-panel>

                <!-- Security Groups Section -->
                <mat-expansion-panel
                    [expanded]="sectionStates.securityGroups"
                    (expandedChange)="sectionStates.securityGroups = $event"
                    *ngIf="securityGroups.length"
                >
                    <mat-expansion-panel-header>
                        <mat-panel-title>
                            <mat-icon class="entity-security-groups">security</mat-icon>
                            Security Groups ({{ securityGroups.length }})
                        </mat-panel-title>
                    </mat-expansion-panel-header>
                    <mat-accordion class="groups-accordion">
                        <mat-expansion-panel *ngFor="let sg of securityGroups">
                            <mat-expansion-panel-header>
                                <mat-panel-title>{{sg.name | normalize}}</mat-panel-title>
                                <mat-panel-description>{{sg.features?.length || 0}} features</mat-panel-description>
                            </mat-expansion-panel-header>
                            <div class="sg-features" *ngIf="sg.features?.length; else noSgFeatures">
                                <div *ngFor="let feature of sg.features" class="feature-row">
                                    <span class="feature-name">{{feature | normalize}}</span>
                                    <div
                                        class="permissions-badges"
                                        *ngIf="sg.featureDetails?.[feature]?.permissions?.length"
                                    >
                                        <span
                                            class="perm-badge"
                                            *ngFor="let perm of sg.featureDetails[feature].permissions"
                                        >
                                            {{perm | normalize}}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <ng-template #noSgFeatures>
                                <p class="no-features-msg">No features assigned to this security group.</p>
                            </ng-template>
                        </mat-expansion-panel>
                    </mat-accordion>
                </mat-expansion-panel>

                <!-- Positions Section -->
                <mat-expansion-panel
                    [expanded]="sectionStates.positions"
                    (expandedChange)="sectionStates.positions = $event"
                    *ngIf="positions.length"
                >
                    <mat-expansion-panel-header>
                        <mat-panel-title>
                            <mat-icon class="entity-positions">place</mat-icon>
                            Positions ({{ positions.length }})
                        </mat-panel-title>
                    </mat-expansion-panel-header>
                    <div class="positions-grid">
                        <div
                            class="position-chip"
                            [class.is-post]="pos.partType === 'post'"
                            *ngFor="let pos of positions"
                        >
                            <mat-icon>location_on</mat-icon>
                            <span>{{pos.name | normalize}}</span>
                        </div>
                    </div>
                </mat-expansion-panel>

                <!-- Policies Section -->
                <mat-expansion-panel
                    [expanded]="sectionStates.policies"
                    (expandedChange)="sectionStates.policies = $event"
                    *ngIf="policies.length"
                >
                    <mat-expansion-panel-header>
                        <mat-panel-title>
                            <mat-icon class="entity-policies">policy</mat-icon>
                            Policies ({{ policies.length }})
                        </mat-panel-title>
                    </mat-expansion-panel-header>
                    <div class="policies-grid">
                        <div class="policy-chip-with-actions" *ngFor="let policy of policies">
                            <mat-icon class="policy-icon">description</mat-icon>
                            <span class="policy-name">{{policy.name | normalize}}</span>
                            <div class="policy-actions">
                                <button
                                    mat-icon-button
                                    class="policy-action-btn view-btn"
                                    matTooltip="View Policy Details"
                                    (click)="viewPolicy(policy)"
                                >
                                    <mat-icon>visibility</mat-icon>
                                </button>
                                <button
                                    mat-icon-button
                                    class="policy-action-btn edit-btn"
                                    matTooltip="Edit Policy"
                                    (click)="editPolicy(policy)"
                                >
                                    <mat-icon>edit</mat-icon>
                                </button>
                            </div>
                        </div>
                    </div>
                </mat-expansion-panel>

                <!-- Features Section -->
                <mat-expansion-panel
                    [expanded]="sectionStates.features"
                    (expandedChange)="sectionStates.features = $event"
                    *ngIf="features.length"
                >
                    <mat-expansion-panel-header>
                        <mat-panel-title>
                            <mat-icon class="entity-features">star</mat-icon>
                            Assigned Features ({{ features.length }})
                        </mat-panel-title>
                    </mat-expansion-panel-header>
                    <mat-chip-set class="features-chips entity-features-chips">
                        <mat-chip *ngFor="let feature of features"> {{feature | normalize}} </mat-chip>
                    </mat-chip-set>
                </mat-expansion-panel>

                <!-- Feature Permissions Section -->
                <mat-expansion-panel
                    [expanded]="sectionStates.featurePermissions"
                    (expandedChange)="sectionStates.featurePermissions = $event"
                    *ngIf="featureDetailsList.length"
                >
                    <mat-expansion-panel-header>
                        <mat-panel-title>
                            <mat-icon class="entity-permissions">verified_user</mat-icon>
                            Feature Permissions ({{ featureDetailsList.length }})
                        </mat-panel-title>
                    </mat-expansion-panel-header>
                    <mat-accordion class="permissions-accordion">
                        <mat-expansion-panel *ngFor="let fd of featureDetailsList">
                            <mat-expansion-panel-header>
                                <mat-panel-title>{{fd.featureKey | normalize}}</mat-panel-title>
                                <mat-panel-description
                                    >{{fd.permissions?.length || 0}} permissions</mat-panel-description
                                >
                            </mat-expansion-panel-header>
                            <mat-chip-set class="entity-permissions-chips">
                                <mat-chip *ngFor="let perm of fd.permissions">{{perm | normalize}}</mat-chip>
                            </mat-chip-set>
                        </mat-expansion-panel>
                    </mat-accordion>
                </mat-expansion-panel>

                <!-- Menu Preview Section -->
                <mat-expansion-panel
                    [expanded]="sectionStates.menuPreview"
                    (expandedChange)="sectionStates.menuPreview = $event"
                    *ngIf="menuPreview.length > 0"
                >
                    <mat-expansion-panel-header>
                        <mat-panel-title>
                            <mat-icon class="entity-hats">menu</mat-icon>
                            Menu Preview ({{ menuPreview.length }})
                            <span class="menu-preview-subtitle">(What Users Wearing This Hat Will See)</span>
                        </mat-panel-title>
                    </mat-expansion-panel-header>

                    <div class="menu-preview">
                        <div *ngFor="let group of menuPreview" class="menu-group">
                            <div class="menu-group-header">{{ group.section }}</div>
                            <div class="menu-items">
                                <div *ngFor="let item of group.items" class="menu-item">
                                    <mat-icon>{{ item.icon }}</mat-icon>
                                    <span>{{ item.name }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </mat-expansion-panel>
            </mat-accordion>

            <!-- No Data -->
            <div
                class="no-data"
                *ngIf="!usersOnHat.length && !securityGroups.length && !positions.length && !features.length && !policies.length"
            >
                <mat-icon>info_outline</mat-icon>
                <p>No additional details available for this hat.</p>
            </div>
        </main>
    </div>
</mat-dialog-content>

<mat-dialog-actions align="end">
    <button mat-flat-button mat-dialog-close color="primary">Close</button>
</mat-dialog-actions>
